import { useLocalStorageState } from '@frontend/common';
import { Application, Feature, Functionality } from '@frontend/functionality/types';

import { LicensedNavbarItemProps } from './licensed-navbar-item.component';

interface ViewProps {
    link: string;
    expanded: boolean;
}

const useLicensedNavbarItem = (props: LicensedNavbarItemProps): ViewProps => {
    const [navbarPinned] = useLocalStorageState('navbar-pinned', true);
    const [hovered] = useLocalStorageState('navbar-hovered', false);

    const link = getLink(props.functionality) || `/${props.functionality.application}/${props.functionality.id}`;

    return {
        link,
        expanded: navbarPinned || hovered
    };
};

export default useLicensedNavbarItem;

export function getLink(functionality: Functionality): string | null {
    if (functionality.application === Application.LAUNDRY) {
        if (functionality.feature === Feature.TRANSACTIONS) {
            return '/laundry/shipments';
        } else if (functionality.feature === Feature.INBOUND_TRANSACTION) {
            return '/laundry/receive_dirty_laundry';
        } else if (functionality.feature === Feature.OUTBOUND_TRANSACTION) {
            return '/laundry/send_clean_laundry';
        } else if (functionality.feature === Feature.SCANNER) {
            return '/laundry/barcode_scanner';
        } else if (functionality.feature === Feature.EQUIPMENTS) {
            return '/laundry/items';
        } else if (functionality.feature === Feature.LABELS) {
            return '/laundry/labels';
        }
    } else if (functionality.application === Application.PUDO) {
        if (functionality.feature === Feature.TRANSACTIONS) {
            return '/pudo/transactions';
        } else if (functionality.feature === Feature.TRANSACTIONS_COLLIS) {
            return '/pudo/transactions_collis';
        } else if (functionality.feature === Feature.COLLIS) {
            return '/pudo/collis';
        } else if (functionality.feature === Feature.INBOUND_TRANSACTION) {
            return '/pudo/awaiting_pick_up';
        } else if (functionality.feature === Feature.OUTBOUND_TRANSACTION) {
            return '/pudo/awaiting_drop_off';
        } else if (functionality.feature === Feature.DASHBOARD) {
            return '/pudo/dashboard';
        }
    } else if (functionality.application === Application.ANALYTICS) {
        if (functionality.feature === Feature.REPORTS) {
            return '/analytics/reports';
        } else if (functionality.feature === Feature.INSIGHTS) {
            return '/analytics/insights';
        } else if (functionality.feature === Feature.SCHEDULES) {
            return '/analytics/schedules';
        }
    } else if (functionality.application === Application.USERS) {
        if (functionality.feature === Feature.USERS) {
            return '/users/users';
        } else if (functionality.feature === Feature.USER_GROUPS) {
            return '/users/groups';
        } else if (functionality.feature === Feature.CERTIFICATES) {
            return '/users/certificates';
        } else if (functionality.feature === Feature.INVITES) {
            return '/users/invites';
        }
    } else if (functionality.application === Application.AUTHORIZATION) {
        if (functionality.feature === Feature.ROLES) {
            return '/authorization/roles';
        } else if (functionality.feature === Feature.PERMISSIONS) {
            return '/authorization/permissions';
        }
    } else if (functionality.application === Application.ORGANIZATION) {
        if (functionality.feature === Feature.INVITES) {
            return '/organization/invites';
        }
    } else if (functionality.application === Application.ADDRESS_BOOK) {
        if (functionality.feature === Feature.CONTACTS) {
            return '/address_book/contacts';
        }
    } else if (functionality.application === Application.VENDING) {
        if (functionality.feature === Feature.STOCK) {
            return '/vending/stock';
        } else if (functionality.feature === Feature.OUTBOUND_TRANSACTION) {
            return '/vending/consumptions';
        } else if (functionality.feature === Feature.INBOUND_TRANSACTION) {
            return '/vending/replenishments';
        } else if (functionality.feature === Feature.DASHBOARD) {
            return '/vending/dashboard';
        } else if (functionality.feature === Feature.RETURN_TRANSACTION) {
            return '/vending/returns';
        } else if (functionality.feature === Feature.RECYCLE_TRANSACTION) {
            return '/vending/recycles';
        } else if (functionality.feature === Feature.TRANSACTIONS) {
            return '/vending/transactions';
        } else if (functionality.feature === Feature.REVIEWS) {
            return '/vending/reviews';
        }
    } else if (functionality.application === Application.SHOP) {
        if (functionality.feature === Feature.OUTBOUND_TRANSACTION) {
            return '/shop/shop';
        }
        if (functionality.feature === Feature.ORDERS) {
            return '/shop/orders';
        }
    } else if (functionality.application === Application.LENDING) {
        if (functionality.feature === Feature.DASHBOARD) {
            return '/lending/dashboard';
        } else if (functionality.feature === Feature.INVENTORY) {
            return '/lending/inventory';
        } else if (functionality.feature === Feature.OUTBOUND_TRANSACTION) {
            return '/lending/utilizations';
        } else if (functionality.feature === Feature.CERTIFICATES) {
            return '/lending/certified_equipments';
        } else if (functionality.feature === Feature.STOCK) {
            return '/lending/stock';
        } else if (functionality.feature === Feature.TRANSACTIONS) {
            return '/lending/transactions';
        }
    } else if (functionality.application === Application.HARDWARE) {
        if (functionality.feature === Feature.GATES) {
            return '/hardware/gates';
        } else if (functionality.feature === Feature.ACCESS_CONTROLLERS) {
            return '/hardware/access-controllers';
        } else if (functionality.feature === Feature.INFRASTRUCTURE) {
            return '/hardware/infrastructure';
        } else if (functionality.feature === Feature.DASHBOARD) {
            return '/hardware/dashboard';
        }
    } else if (functionality.application === Application.SPOTS) {
        if (functionality.feature === Feature.LAYOUT) {
            return '/spots/contents';
        } else if (functionality.feature === Feature.SPOTS) {
            return '/spots/spots';
        } else if (functionality.feature === Feature.MODULES) {
            return '/spots/modules';
        } else if (functionality.feature === Feature.SLOTS) {
            return '/spots/slots';
        }
    } else if (functionality.application === Application.CATALOGUE) {
        if (functionality.feature === Feature.PRODUCTS) {
            return '/catalogue/products';
        } else if (functionality.feature === Feature.ATTRIBUTES) {
            return '/catalogue/attributes';
        } else if (functionality.feature === Feature.CATEGORIES) {
            return '/catalogue/categories';
        } else if (functionality.feature === Feature.KITS) {
            return '/catalogue/kits';
        }
    } else if (functionality.application === Application.NOTIFICATIONS) {
        if (functionality.feature === Feature.NOTIFICATIONS) {
            return '/notifications/notifications';
        } else if (functionality.feature === Feature.TEMPLATES) {
            return '/notifications/templates';
        }
    } else if (functionality.application === Application.CONSTRAINTS) {
        if (functionality.feature === Feature.CONSTRAINTS) {
            return '/constraints/constraints';
        }
    } else if (functionality.application === Application.REQUIREMENTS) {
        if (functionality.feature === Feature.REQUIREMENTS) {
            return '/requirements/requirements';
        }
    } else if (functionality.application === Application.NIVALIZ) {
        if (functionality.feature === Feature.DASHBOARD) {
            return '/nivaliz/dashboard';
        } else if (functionality.feature === Feature.INBOUND_TRANSACTION) {
            // Coffins (inbound transactions)
            return '/nivaliz/coffins';
        } else if (functionality.feature === Feature.OUTBOUND_TRANSACTION) {
            // Urns (outbound transactions)
            return '/nivaliz/urns';
        } else if (functionality.feature === Feature.TRANSACTIONS) {
            // Nivaliz transactions overview
            return '/nivaliz/transactions';
        } else if (functionality.feature === Feature.DECEASED) {
            return '/nivaliz/deceaseds';
        }
    } else if (functionality.application === Application.BUDGETS) {
        if (functionality.feature === Feature.USES) {
            return '/budgets/uses';
        }
    }
    return null;
}
